import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';
import { normalizePhoneDigits } from '../../util/ehr';

// Phone input wrapper that handles conversion between raw digits and formatted display
const EhrPhoneInput = ({ value, onChange, ...props }) => {
  const inputRef = useRef(null);
  // Digits left of the caret at change time; null when no user edit is pending
  const caretDigitsRef = useRef(null);

  // Format the value for display
  const formatPhone = (val) => {
    if (!val) return '';
    const digits = normalizePhoneDigits(val);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const formattedValue = formatPhone(value);

  // Index just after the Nth digit of the formatted string, skipping dashes
  const caretIndexForDigits = (digitCount) => {
    let pos = 0;
    let digitsSeen = 0;
    while (pos < formattedValue.length && digitsSeen < digitCount) {
      if (/\d/.test(formattedValue[pos])) digitsSeen += 1;
      pos += 1;
    }
    return pos;
  };

  const handleChange = (e) => {
    const { value: rawValue, selectionStart } = e.target;
    const caretDigits = rawValue
      .slice(0, selectionStart === null ? rawValue.length : selectionStart)
      .replace(/\D/g, '').length;
    caretDigitsRef.current = caretDigits;
    const digits = normalizePhoneDigits(rawValue);
    // Call onChange with the raw digits (this is what gets stored in form)
    if (onChange) {
      onChange(digits);
    }
    // A no-op edit (backspacing a dash, an ignored eleventh digit) never
    // re-renders, but React's controlled-input restore still rewrites the DOM
    // value and kicks the caret to the end. Snap both back here instead.
    if (digits === normalizePhoneDigits(value)) {
      const node = e.target;
      node.value = formattedValue;
      const pos = caretIndexForDigits(caretDigits);
      node.setSelectionRange(pos, pos);
      caretDigitsRef.current = null;
    }
  };

  // Re-formatting replaces the input value, which kicks the browser caret to the
  // end of the field; restore it to just after the last digit the user typed.
  // Keyed to formattedValue: the browser only resets the caret when the displayed
  // string actually changes, and an edit that leaves it unchanged must not apply
  // its stale caret position on a later unrelated re-render.
  useLayoutEffect(() => {
    if (caretDigitsRef.current === null) return;
    const node = inputRef.current && inputRef.current.input;
    if (node && document.activeElement === node) {
      const pos = caretIndexForDigits(caretDigitsRef.current);
      node.setSelectionRange(pos, pos);
    }
    caretDigitsRef.current = null;
  }, [formattedValue]);

  return (
    <Input
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...props}
      ref={inputRef}
      value={formattedValue}
      onChange={handleChange}
      placeholder="555-123-4567"
    />
  );
};

EhrPhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

EhrPhoneInput.defaultProps = {
  value: undefined,
  onChange: undefined,
};

export default EhrPhoneInput;
